// HRMS — Main shell page (replaces /people).
// Zoho People-style layout with left rail + company switcher header.
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import hr from './hrms/api';
import { HR_NAV, BSC_ORANGE, BSC_GREEN } from './hrms/ui';

import Dashboard from './hrms/sections/Dashboard';
import { Employees, Directory } from './hrms/sections/People';
import Org from './hrms/sections/Org';
import { Attendance, Leaves, Holidays } from './hrms/sections/Time';
import { Hiring, Onboarding, Performance } from './hrms/sections/Talent';
import { SalaryStructures, PayrollRuns, Reimbursements } from './hrms/sections/Payroll';
import { Announcements, Helpdesk, CompaniesSetup } from './hrms/sections/Selfservice';

const SECTIONS = {
  dashboard: Dashboard,
  employees: Employees,
  directory: Directory,
  org: Org,
  attendance: Attendance,
  leaves: Leaves,
  holidays: Holidays,
  hiring: Hiring,
  onboarding: Onboarding,
  performance: Performance,
  salary: SalaryStructures,
  payroll: PayrollRuns,
  reimbursements: Reimbursements,
  announcements: Announcements,
  helpdesk: Helpdesk,
  companies: CompaniesSetup,
  settings: () => <div className="text-white/60">Configuration · Coming soon (Workflows, Approvals, Roles)</div>,
};

export default function People() {
  const [section, setSection] = useState('dashboard');
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState(() => localStorage.getItem('hrms_company_id') || '');
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const reloadCompanies = async () => {
    try {
      const list = await hr.get('/companies');
      setCompanies(list);
      if (!companyId && list.length) {
        setCompanyId(list[0].id);
        localStorage.setItem('hrms_company_id', list[0].id);
      }
    } catch (e) { console.error(e); }
  };
  useEffect(() => { reloadCompanies(); }, []);

  useEffect(() => {
    if (companyId) localStorage.setItem('hrms_company_id', companyId);
  }, [companyId]);

  const Active = useMemo(() => SECTIONS[section] || Dashboard, [section]);
  const currentCompany = companies.find((c) => c.id === companyId);

  return (
    <div className="min-h-screen flex bg-black text-white" data-testid="people-shell">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r border-white/10 bg-neutral-950/90 flex flex-col">
        <div className="px-5 py-5 border-b border-white/10">
          <button onClick={() => navigate('/')} className="flex items-center gap-2" data-testid="people-back-home">
            <span className="text-[18px] tracking-[0.3em] font-light text-white/90" style={{ fontFamily: "'Monoton', cursive" }}>
              <span style={{ color: BSC_ORANGE }}>P</span>
              <span>E</span>
              <span style={{ color: BSC_GREEN }}>O</span>
              <span>P</span>
              <span style={{ color: BSC_ORANGE }}>L</span>
              <span>E</span>
            </span>
          </button>
          <p className="text-[10px] tracking-[0.3em] uppercase text-white/35 mt-1">HRMS · Workforce</p>
        </div>

        <nav className="flex-1 overflow-y-auto py-3">
          {HR_NAV.map((it, i) => {
            if (it.kind === 'heading') return <div key={i} className="px-5 mt-4 mb-2 text-[9px] tracking-[0.3em] uppercase text-white/30">{it.label}</div>;
            const active = section === it.key;
            return (
              <button key={it.key} onClick={() => setSection(it.key)}
                className={`w-full flex items-center gap-3 px-5 py-2 text-left text-sm transition relative ${active ? 'bg-white/[0.06] text-white' : 'text-white/55 hover:text-white hover:bg-white/[0.03]'}`}
                data-testid={`people-nav-${it.key}`}>
                {active && <span className="absolute left-0 top-0 bottom-0 w-[2px]" style={{ backgroundColor: BSC_ORANGE }} />}
                <span>{it.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="border-t border-white/10 p-4 text-xs">
          <div className="text-white/70 truncate">{user?.name}</div>
          <div className="text-white/35 truncate">{user?.email}</div>
          <button onClick={async () => { await logout(); navigate('/'); }} className="mt-2 text-white/40 hover:text-white text-[11px]">Log out →</button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-y-auto">
        {/* Company switcher header */}
        <div className="sticky top-0 z-20 border-b border-white/10 bg-black/80 backdrop-blur px-8 py-3 flex items-center gap-3">
          <span className="text-[10px] tracking-[0.25em] uppercase text-white/40">Company</span>
          <select
            className="bg-white/[0.04] border border-white/10 rounded px-3 py-1.5 text-sm text-white"
            value={companyId} onChange={(e) => setCompanyId(e.target.value)}
            data-testid="people-company-switcher"
          >
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.employees ?? 0})</option>)}
          </select>
          {currentCompany?.gstin && <span className="text-xs text-white/40">GSTIN: <span className="text-white/70 font-mono">{currentCompany.gstin}</span></span>}
        </div>

        <div className="max-w-[1500px] mx-auto px-8 py-8">
          {companyId && <Active companyId={companyId} companies={companies} onChange={reloadCompanies} onNav={setSection} />}
          {!companyId && <p className="text-white/40 text-sm">Loading companies…</p>}
        </div>
      </main>
    </div>
  );
}
